import {
  CircleCheck,
  Gauge,
  PauseCircle,
  ShieldAlert,
  ThumbsDown,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { AttentionItem } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDuration } from "@/lib/format";
import { cn } from "@/lib/utils";

const icons: Record<AttentionItem["kind"], LucideIcon> = {
  low_activity: Gauge,
  unproductive: ThumbsDown,
  long_idle: PauseCircle,
  budget: Wallet,
  anomaly: ShieldAlert,
};

/** Employees and projects that need a look: activity, unproductive time, idle, budgets, anomalies. */
export function AttentionCard({ items, lang }: { items: AttentionItem[] | undefined; lang: string }) {
  const { t } = useTranslation();
  const critical = items?.filter((i) => i.severity === "critical").length ?? 0;
  return (
    <Card className="flex flex-col overflow-hidden">
      <CardHeader className="flex-row items-center justify-between pb-4">
        <CardTitle>{t("dashboard.attention.title")}</CardTitle>
        {items && items.length > 0 && (
          <Badge variant={critical > 0 ? "danger" : "warning"}>{items.length}</Badge>
        )}
      </CardHeader>
      {!items ? (
        <div className="flex flex-col gap-3 px-5 pb-5">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-5 pb-8 text-center text-muted">
          <CircleCheck className="size-8 text-success" aria-hidden="true" />
          {t("dashboard.attention.empty")}
        </div>
      ) : (
        <ul className="max-h-96 overflow-y-auto">
          {items.map((item, i) => (
            <AttentionRow
              key={`${item.kind}-${item.user_id ?? item.project?.id}-${i}`}
              item={item}
              lang={lang}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function AttentionRow({ item, lang }: { item: AttentionItem; lang: string }) {
  const { t } = useTranslation();
  const Icon = icons[item.kind];
  const critical = item.severity === "critical";
  const text = (() => {
    switch (item.kind) {
      case "low_activity":
        return t("dashboard.attention.lowActivity", { percent: item.value });
      case "unproductive":
        return t("dashboard.attention.unproductive", {
          time: formatDuration(item.value, lang),
          percent: item.percent ?? 0,
        });
      case "long_idle":
        return t("dashboard.attention.longIdle", { time: formatDuration(item.value, lang) });
      case "budget":
        return t("dashboard.attention.budget", { percent: item.value });
      case "anomaly":
        return t("dashboard.attention.anomaly", {
          count: item.value,
          reason: t(`anomalies.kinds.${item.anomaly_kind ?? "monotonous_input"}`),
        });
    }
  })();
  const link =
    item.kind === "anomaly"
      ? "/reports/anomalies"
      : item.project
        ? `/projects/${item.project.id}`
        : `/activity/${item.user_id ?? ""}`;
  return (
    <li className="flex gap-3 border-t px-5 py-3.5">
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg",
          critical ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning",
        )}
      >
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate font-medium">
          {item.project ? t("dashboard.attention.project", { name: item.project.name }) : item.user_name}
        </span>
        <span className="text-muted">
          <span className="sr-only">
            {critical ? t("dashboard.attention.critical") : t("dashboard.attention.warning")}:{" "}
          </span>
          {text}
        </span>
        <Link to={link} className="mt-1 w-fit font-medium text-primary hover:underline">
          {item.kind === "anomaly"
            ? t("dashboard.attention.openAnomalies")
            : item.project
              ? t("dashboard.attention.openProject")
              : t("dashboard.openTimeline")}
        </Link>
      </div>
    </li>
  );
}
